/**
 * Tab Navigation Hook
 * Handles mouse gestures and keyboard shortcuts for tab navigation
 *
 * Features:
 * - Mouse back/forward buttons (Button 3/4) navigate between tabs
 * - Ctrl+Tab / Ctrl+Shift+Tab - Navigate to next/previous tab
 * - Ctrl+PageUp / Ctrl+PageDown - Navigate to previous/next tab
 * - Alt+Left / Alt+Right (Mac: Cmd+[ / Cmd+]) - Navigate page history in active tab
 */

import { useEffect, useCallback } from 'react';
import { useLayoutStore } from '../store/layoutStore';

/**
 * Get the next tab ID in a given direction
 */
function getAdjacentTabId(
  tabs: { id: string; isActive: boolean }[],
  activeTabId: string | undefined,
  direction: 'next' | 'previous',
): string | undefined {
  if (tabs.length === 0) return undefined;
  if (!activeTabId) return tabs[0]?.id;

  const currentIndex = tabs.findIndex((tab) => tab.id === activeTabId);
  if (currentIndex === -1) return tabs[0]?.id;

  if (direction === 'next') {
    // Wrap around to first tab if at end
    const nextIndex = (currentIndex + 1) % tabs.length;
    return tabs[nextIndex]?.id;
  } else {
    // Wrap around to last tab if at beginning
    const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    return tabs[prevIndex]?.id;
  }
}

/**
 * Hook to enable tab navigation via mouse gestures and keyboard shortcuts
 */
export function useTabNavigation() {
  const tabs = useLayoutStore((state) => state.mainContentTabs);
  const activeTabId = useLayoutStore((state) => state.activeMainContentTabId);
  const setActiveTab = useLayoutStore((state) => state.setActiveMainContentTab);
  const goBackInActiveMainContentTab = useLayoutStore(
    (state) => state.goBackInActiveMainContentTab,
  );
  const goForwardInActiveMainContentTab = useLayoutStore(
    (state) => state.goForwardInActiveMainContentTab,
  );

  const navigateToTab = useCallback(
    (direction: 'next' | 'previous') => {
      const nextTabId = getAdjacentTabId(tabs, activeTabId, direction);
      if (nextTabId && nextTabId !== activeTabId) {
        setActiveTab(nextTabId);
      }
    },
    [tabs, activeTabId, setActiveTab],
  );

  const navigateHistory = useCallback(
    (direction: 'back' | 'forward') => {
      if (direction === 'back') {
        goBackInActiveMainContentTab();
      } else {
        goForwardInActiveMainContentTab();
      }
    },
    [goBackInActiveMainContentTab, goForwardInActiveMainContentTab],
  );

  // Handle mouse back/forward buttons
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      // Button 3 = Back, Button 4 = Forward (on mice with extra buttons)
      // Some browsers use button values differently, so we handle both common cases
      if (event.button === 3) {
        // Back button
        event.preventDefault();
        event.stopPropagation();
        navigateHistory('back');
      } else if (event.button === 4) {
        // Forward button
        event.preventDefault();
        event.stopPropagation();
        navigateHistory('forward');
      }
    };

    // Use capture phase to intercept before the event reaches other handlers
    window.addEventListener('mousedown', handleMouseDown, { capture: true });

    // Also prevent the default browser back/forward behavior for these buttons
    const handleMouseUp = (event: MouseEvent) => {
      if (event.button === 3 || event.button === 4) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('mouseup', handleMouseUp, { capture: true });

    // Some browsers trigger auxclick for middle/side buttons
    const handleAuxClick = (event: MouseEvent) => {
      if (event.button === 3 || event.button === 4) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('auxclick', handleAuxClick, { capture: true });

    return () => {
      window.removeEventListener('mousedown', handleMouseDown, {
        capture: true,
      });
      window.removeEventListener('mouseup', handleMouseUp, { capture: true });
      window.removeEventListener('auxclick', handleAuxClick, { capture: true });
    };
  }, [navigateHistory]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if event was already handled or we're in an input
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      const isMac =
        typeof navigator !== 'undefined' && navigator.platform.includes('Mac');
      const isModKey = isMac ? event.metaKey : event.ctrlKey;
      const isAltKey = event.altKey;

      // Ctrl+Tab / Ctrl+Shift+Tab - Navigate tabs (works even in inputs)
      if (event.ctrlKey && event.key === 'Tab') {
        event.preventDefault();
        if (event.shiftKey) {
          navigateToTab('previous');
        } else {
          navigateToTab('next');
        }
        return;
      }

      // Skip remaining shortcuts if in an input (except Tab which we handled above)
      if (isInput) return;

      // Ctrl+PageUp / Ctrl+PageDown - Navigate tabs
      if (isModKey && (event.key === 'PageUp' || event.key === 'PageDown')) {
        event.preventDefault();
        if (event.key === 'PageUp') {
          navigateToTab('previous');
        } else {
          navigateToTab('next');
        }
        return;
      }

      // Alt+Left / Alt+Right (Mac: Cmd+[ / Cmd+]) - Navigate history
      if (isMac) {
        if (isModKey && event.key === '[') {
          event.preventDefault();
          navigateHistory('back');
          return;
        }
        if (isModKey && event.key === ']') {
          event.preventDefault();
          navigateHistory('forward');
          return;
        }
      } else {
        if (isAltKey && event.key === 'ArrowLeft') {
          event.preventDefault();
          navigateHistory('back');
          return;
        }
        if (isAltKey && event.key === 'ArrowRight') {
          event.preventDefault();
          navigateHistory('forward');
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigateToTab, navigateHistory]);

  return {
    navigateToTab,
    navigateHistory,
  };
}
